import React, { useState, useEffect } from 'react';

/**
 * SMS 본인확인 모달
 *
 * Props:
 *   open         - boolean, 모달 표시 여부
 *   onClose      - () => void, 취소 버튼 / 외부 클릭 시 호출
 *   maskedPhone  - string, 마스킹된 전화번호 표시용 (예: 010-****-1234)
 *   onSend       - async () => void, 인증번호 발송 요청 (실패 시 throw)
 *   onVerify     - async (code: string) => boolean, 인증 확인 (true=성공)
 *   t            - translation object
 */
export default function SmsVerifyModal({
  open,
  onClose,
  maskedPhone,
  onSend,
  onVerify,
  t,
}) {
  const [smsSent, setSmsSent] = useState(false);
  const [code, setCode] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  // 모달이 열릴 때마다 내부 상태 초기화
  useEffect(() => {
    if (open) {
      setSmsSent(false);
      setCode('');
      setMessage('');
      setIsError(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSend = async () => {
    try {
      setSendLoading(true);
      setMessage('');
      await onSend();
      setSmsSent(true);
      setCode('');
      setMessage(t.smsVerifySent);
      setIsError(false);
    } catch {
      setMessage(t.smsVerifySendFail);
      setIsError(true);
    } finally {
      setSendLoading(false);
    }
  };

  const handleVerify = async () => {
    const digits = code.replace(/[^0-9]/g, '');
    if (digits.length !== 6) {
      setMessage('인증번호 6자리를 입력해 주세요.');
      setIsError(true);
      return;
    }
    try {
      setVerifyLoading(true);
      setMessage('');
      const ok = await onVerify(digits);
      if (ok) {
        setMessage(t.smsVerifySuccess);
        setIsError(false);
        // 성공 메시지를 잠깐 보여준 뒤 자동으로 닫는다.
        setTimeout(() => onClose(), 900);
      } else {
        setMessage(t.smsVerifyFail);
        setIsError(true);
      }
    } catch {
      setMessage(t.smsVerifyFail);
      setIsError(true);
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '14px',
          width: '100%',
          maxWidth: '360px',
          boxSizing: 'border-box',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 영역 */}
        <div
          style={{
            padding: '24px 24px 0',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '20px',
            marginBottom: '4px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: '#fff0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '14px',
              fontSize: '22px',
            }}
          >
            🔒
          </div>
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: '17px',
              fontWeight: 700,
              color: '#111',
            }}
          >
            {t.smsVerifyTitle}
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: 1.6 }}>
            {t.smsVerifyDesc}
          </p>
        </div>

        {/* 본문 영역 */}
        <div style={{ padding: '20px 24px 24px' }}>
          {/* 전화번호 표시 */}
          {maskedPhone && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                background: '#f7f7f7',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            >
              <span style={{ color: '#888', fontSize: '13px' }}>등록번호</span>
              <span style={{ fontWeight: 700, color: '#222', letterSpacing: '1px' }}>
                {maskedPhone}
              </span>
            </div>
          )}

          {/* 인증번호 발송 버튼 */}
          <button
            type="button"
            onClick={handleSend}
            disabled={sendLoading}
            style={{
              width: '100%',
              padding: '11px 0',
              borderRadius: '8px',
              border: 'none',
              background: smsSent ? '#888' : '#333',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: sendLoading ? 'not-allowed' : 'pointer',
              marginBottom: '12px',
              opacity: sendLoading ? 0.7 : 1,
              boxSizing: 'border-box',
            }}
          >
            {sendLoading ? '발송 중...' : smsSent ? t.smsVerifyResendBtn : t.smsVerifySendBtn}
          </button>

          {/* 인증번호 입력 영역 (발송 후 표시) */}
          {smsSent && (
            <div
              style={{
                borderTop: '1px solid #eee',
                paddingTop: '14px',
                boxSizing: 'border-box',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#888',
                  marginBottom: '6px',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                }}
              >
                {t.authCode}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder={t.smsVerifyCodePlaceholder}
                value={code}
                onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                style={{
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '11px 14px',
                  border: '1.5px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '18px',
                  letterSpacing: '6px',
                  textAlign: 'center',
                  fontWeight: 700,
                  color: '#222',
                  outline: 'none',
                  marginBottom: '10px',
                }}
              />
              <button
                type="button"
                onClick={handleVerify}
                disabled={verifyLoading || code.length !== 6}
                style={{
                  width: '100%',
                  padding: '11px 0',
                  borderRadius: '8px',
                  border: 'none',
                  background: code.length === 6 && !verifyLoading ? '#e74c3c' : '#ccc',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: code.length === 6 && !verifyLoading ? 'pointer' : 'not-allowed',
                  boxSizing: 'border-box',
                  transition: 'background 0.2s',
                }}
              >
                {verifyLoading ? '확인 중...' : t.smsVerifyConfirmBtn}
              </button>
            </div>
          )}

          {/* 메시지 */}
          {message && (
            <p
              style={{
                margin: '12px 0 0',
                fontSize: '13px',
                color: isError ? '#e74c3c' : '#27ae60',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {message}
            </p>
          )}

          {/* 취소 버튼 */}
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              marginTop: '14px',
              padding: '10px 0',
              borderRadius: '8px',
              border: '1.5px solid #ddd',
              background: '#fff',
              color: '#666',
              fontSize: '13px',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}
